import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoButton } from '@atlassian/search-common/rovo-navigation/rovo-button';

import type { PrimaryProductKey } from '../../../common/constants/product-keys';
import { FilterProductIcon } from '../../../common/ui/filters/filter-product-icon';
import { serveJiraNavigationToGNIN } from '../../../common/utils/experiments';
import { serveJiraNinFpsNavigation } from '../../../common/utils/experiments/experiment-exports';
import { useSainJql } from '../../../controllers/smart-answers-intent-store';

import { messages } from './messages';

type SearchPageHeaderProps = {
	primaryProduct?: PrimaryProductKey;
	query?: string;
};

const styles = cssMap({
	headerStyles: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: token('space.050'),
		margin: 'auto',
		width: '100%',
		maxWidth: '100%',
		whiteSpace: 'nowrap',
		paddingTop: token('space.200'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	headerStylesWithLink: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		alignItems: 'end',
		gap: token('space.150'),
	},
	headerText: {
		marginTop: token('space.0'),
		fontWeight: token('font.weight.regular'),
		alignItems: 'center',
		gap: token('space.100'),
		display: 'flex',
		flex: 1,
		minWidth: '0px',
	},
	headerTitle: {
		fontWeight: token('font.weight.bold'),
		flexShrink: 0,
	},
	headerQuery: {
		flex: 1,
		minWidth: '0px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

export const SearchPageHeader = ({
	primaryProduct,
	query,
}: SearchPageHeaderProps): React.JSX.Element | null => {
	const { formatMessage, locale } = useIntl();
	const sainJql = useSainJql();

	const fpsNinNavigation = useMemo(
		() => serveJiraNinFpsNavigation({ primaryProduct, fireLayerExposure: false, locale }),
		[primaryProduct, locale],
	);

	const isButtonCohort = fpsNinNavigation?.cohort === 'button';

	// QS-10204 — interim FPS → GNIN navigation experiment. Memoised so the Statsig
	// exposure fires once per allocation, and only for the eligible (NLQ `button`
	// cohort) population so the exposure denominator stays correctly scoped.
	const gninNavigationCohort = useMemo(
		() => (isButtonCohort ? serveJiraNavigationToGNIN() : 'control'),
		[isButtonCohort],
	);

	if (!isButtonCohort) {
		return null;
	}

	const showWorkItemIcon = gninNavigationCohort === 'work-item-icon';
	const showHeaderLink = gninNavigationCohort === 'header-link';

	const showQueryInHeader =
		!showHeaderLink &&
		!showWorkItemIcon &&
		Boolean(query) &&
		Boolean(fpsNinNavigation.showQueryInHeader);
	const headerLabel = `${formatMessage(messages.searchResults)}${showQueryInHeader ? ':' : ''}`;

	return (
		<Box xcss={cx(styles.headerStyles, showHeaderLink && styles.headerStylesWithLink)}>
			<Box as="h2" xcss={styles.headerText}>
				<Box xcss={styles.headerTitle}>{headerLabel}</Box>
				{showQueryInHeader && <Box xcss={styles.headerQuery}>&#39;{query}&#39;</Box>}
			</Box>
			{showHeaderLink && (
				<RovoButton
					experience="full-page-search"
					sainJql={sainJql}
					appearance="link"
					gninNavigationCohort={gninNavigationCohort}
				/>
			)}
			{!showHeaderLink && (
				<RovoButton
					experience="full-page-search"
					sainJql={sainJql}
					gninNavigationCohort={gninNavigationCohort}
					iconBefore={
						showWorkItemIcon ? (
							<FilterProductIcon showPrimaryProductIcon primaryProduct={primaryProduct} />
						) : undefined
					}
				/>
			)}
		</Box>
	);
};
